import React, { useState, useEffect, useMemo } from "react";
import TransactionApi from "../../Api/TransactionApi";
import SideBar from "../Sidebar/SideBar";
import Branches from "../../Api/Branches";
import SearchableDropdown from "./SearchableDropdown";

function getTodayDate() {
    return new Date().toISOString().split("T")[0];
}

function formatDate(iso) {
    if (!iso) return "-";
    return new Date(iso)
        .toLocaleDateString("en-IN", { day: "2-digit", month: "2-digit", year: "numeric" })
        .split("/").join("-");
}

function formatAmt(amt) {
    return (Number(amt) || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 });
}

const TYPE_LABEL = { 1: "Package", 2: "Discount", 3: "Charges" };
const TYPE_BADGE = { 1: "tx-badge-pkg", 2: "tx-badge-disc", 3: "tx-badge-chg" };

export default function Transaction() {
    const [billData, setBillData] = useState([]);
    const [expandedRow, setExpandedRow] = useState(null);
    const [fromDate, setFromDate] = useState(getTodayDate());
    const [toDate, setToDate] = useState(getTodayDate());
    const [branchHash, setBranchHash] = useState("");
    const [branchList, setBranchList] = useState([]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const res = await TransactionApi.GetTransaction(fromDate, toDate);
                setBillData(Array.isArray(res?.Result) ? res.Result : []);
            } catch (err) {
                console.error(err);
                setBillData([]);
            }
        };
        fetchData();
    }, [fromDate, toDate]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const res = await TransactionApi.GetTransactionByBranch(branchHash, fromDate, toDate);
                setBillData(Array.isArray(res?.Result) ? res.Result : []);
            } catch (err) {
                console.error(err);
                setBillData([]);
            }
        };
        fetchData();
    }, [branchHash]);

    useEffect(() => {
        const fetchBranches = async () => {
            try {
                const res = await Branches.GetAllClients();
                setBranchList(Array.isArray(res?.Result) ? res.Result : []);
            } catch (err) {
                console.error(err);
                setBranchList([]);
            }
        };

        fetchBranches();
    }, []);

    const totals = useMemo(() =>
        billData.reduce((acc, item) => {
            const t = item?.trans1 || {};
            return {
                totalAmt: acc.totalAmt + (Number(t.totalAmt) || 0),
                totalDueAmt: acc.totalDueAmt + (Number(t.totalDueAmt) || 0),
                totalPaidAmt: acc.totalPaidAmt + (Number(t.totalPaidAmt) || 0),
            };
        }, { totalAmt: 0, totalDueAmt: 0, totalPaidAmt: 0 }),
        [billData]);

    const toggle = (id) => setExpandedRow((prev) => (prev === id ? null : id));

    return (
        <div className="Flex-general-client-sidebar">
            <div className="sidebar-gen-width">
                <SideBar />
            </div>

            <div className="Main-general-Client">
                <div className="header-general">
                    <h2 className="general-card-title">Transaction</h2>
                </div>

                <div className="control_radius">
                    {/* Date filters */}
                    <div className="company_control">
                        <div className="stock-im-dep1">
                            <div className="input-box-Imo1">
                                <div className="input-box-mok1">
                                    <SearchableDropdown
                                        options={branchList}
                                        value={branchHash}
                                        onChange={(val) => setBranchHash(val)}
                                        labelField="branchName"
                                        valueField="hashValue"
                                        placeholder="Select Branch"
                                        required
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="stock-im-dep1">
                            <div className="input-box-Imo1 input-height2">
                                <div className="input-box-mok1">
                                    <input
                                        className="modify_im_input mode_category1"
                                        type="date" name="fromDate"
                                        value={fromDate}
                                        onChange={(e) => setFromDate(e.target.value)}
                                    />
                                    <label className="modify_lbl_text3">From Date</label>
                                </div>
                            </div>
                        </div>
                        <div className="stock-im-dep1">
                            <div className="input-box-Imo1 input-height2">
                                <div className="input-box-mok1">
                                    <input
                                        className="modify_im_input mode_category1"
                                        type="date" name="toDate"
                                        value={toDate}
                                        onChange={(e) => setToDate(e.target.value)}
                                    />
                                    <label className="modify_lbl_text3">To Date</label>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Table */}
                    <div className="tx-table-wrap">
                        <table className="tx-table">
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>Vouch No</th>
                                    <th>Vouch Date</th>
                                    <th className="tx-amt">Total Amt</th>
                                    <th className="tx-amt">Due Amt</th>
                                    <th className="tx-amt">Paid Amt</th>
                                    <th>Remarks</th>
                                    <th style={{ textAlign: "center" }}>Detail</th>
                                </tr>
                            </thead>

                            <tbody>
                                {billData.length === 0 ? (
                                    <tr>
                                        <td colSpan={8} style={{ textAlign: "center", padding: "20px" }}>
                                            No Data Found
                                        </td>
                                    </tr>
                                ) : (
                                    billData.map((item, index) => {
                                        const trans1 = item?.trans1 || {};
                                        const trans2 = item?.trans2 || [];
                                        const id = trans1?.id ?? index;
                                        const isExpanded = expandedRow === id;
                                        const rowCls = index % 2 === 0 ? "tx-row-even" : "tx-row-odd";

                                        return (
                                            <React.Fragment key={id}>
                                                {/* ── trans1 row ── */}
                                                <tr
                                                    className={rowCls}
                                                    style={{ cursor: "pointer" }}
                                                    onClick={() => toggle(id)}
                                                >
                                                    <td className="tx-amt">{index + 1}</td>
                                                    <td className="tx-amt">{trans1.voucherNo}</td>
                                                    <td>{formatDate(trans1.voucherDate)}</td>
                                                    <td className="tx-amt">{formatAmt(trans1.totalAmt)}</td>
                                                    <td className="tx-amt">{formatAmt(trans1.totalDueAmt)}</td>
                                                    <td className="tx-amt">{formatAmt(trans1.totalPaidAmt)}</td>
                                                    <td className="tx-amt">{trans1.remarks}</td>
                                                    <td style={{ textAlign: "center" }}>
                                                        <button className="tx-expand-btn" aria-label="toggle detail">
                                                            {isExpanded ? "▾" : "▸"}
                                                        </button>
                                                    </td>
                                                </tr>

                                                {/* ── trans2 expanded panel ── */}
                                                {isExpanded && (
                                                    <tr className="tx-detail-row">
                                                        <td colSpan={8}>
                                                            <div className="tx-detail-inner">
                                                                {trans2.length === 0 ? (
                                                                    <p style={{ margin: 0, fontSize: 12, color: "#888" }}>
                                                                        No item details found.
                                                                    </p>
                                                                ) : (
                                                                    <>
                                                                        <table className="tx-sub-table">
                                                                            <thead>
                                                                                <tr>
                                                                                    <th>#</th>
                                                                                    <th>Type</th>
                                                                                    <th>Name</th>
                                                                                    <th className="tx-amt">Price</th>
                                                                                    <th className="tx-amt">Total</th>
                                                                                    <th className="tx-amt">Remark</th>
                                                                                </tr>
                                                                            </thead>
                                                                            <tbody>
                                                                                {trans2.map((sub, i) => (
                                                                                    <tr key={i}>
                                                                                        <td style={{ color: "#888", fontSize: 11 }}>{i + 1}</td>
                                                                                        <td>
                                                                                            <span className={`tx-type-badge ${TYPE_BADGE[sub.masterType] || "tx-badge-pay"}`}>
                                                                                                {TYPE_LABEL[sub.masterType] || "Payment"}
                                                                                            </span>
                                                                                        </td>
                                                                                        <td>{sub.name}</td>
                                                                                        <td className="tx-amt">{formatAmt(sub.price)}</td>
                                                                                        <td className="tx-amt">{formatAmt(sub.totalAmt)}</td>
                                                                                        <td style={{ fontSize: 11, color: "#5a6a80" }}>{sub.remark}</td>
                                                                                    </tr>
                                                                                ))}
                                                                            </tbody>
                                                                        </table>
                                                                    </>
                                                                )}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )}
                                            </React.Fragment>
                                        );
                                    })
                                )}
                            </tbody>

                            <tfoot>
                                <tr>
                                    <td colSpan={3} className="tx-foot-label">Total</td>
                                    <td className="tx-foot-amt">{formatAmt(totals.totalAmt)}</td>
                                    <td className="tx-foot-amt">{formatAmt(totals.totalDueAmt)}</td>
                                    <td className="tx-foot-amt">{formatAmt(totals.totalPaidAmt)}</td>
                                    <td colSpan={2} />
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
}